import { tools as toolEnums } from '../enums';
import subpixel from '../util/canvasSubpixelOffset';
import LineTool from './LineTool';

const { FibToolLabelFormat } = toolEnums.FIBONACCI;

/** Fibonacci retracement tool */
export default class FibonacciLineTool extends LineTool {
	constructor(core, settings, flavor) {
		super(core, settings);

		if (flavor && flavor.toLowerCase() === 'extended') {
			this.isExtended = true;
		} else {
			this.isExtended = false;
		}
		this.thresholds = this.params.style.thresholds;
		this.levelLines = {
			x: null,
			y: [],
			isFlipped: false
		};
		this.BOUNDING_BOX_HEIGHT = 5;
	}

	_getDefaultParams() {
		return {
			id: 'fibonacci',
			name: 'Fibonacci Retracement',
			showExtensions: this.isExtended,
			labelFormat: FibToolLabelFormat.DECIMAL,
			style: this.core.getStyle('tool.fibonacci')
		};
	}

	isWithinFibLines(mousePanel, coords) {
		let boundingBox = {};
		const mouseRect = {
			x: mousePanel[0],
			y: mousePanel[1],
			width: 1,
			height: 1
		};

		for (let i = 0; i < this.levelLines.y.length; i++) {
			if (this.levelLines.isFlipped) {
				boundingBox = {
					x: 0,
					y: this.levelLines.y[i],
					width: coords[0][0],
					height: this.BOUNDING_BOX_HEIGHT
				};
			} else {
				boundingBox = {
					x: this.levelLines.x,
					y: this.levelLines.y[i],
					width: this.panel.size.width + this.panel.size.padding.left,
					height: this.BOUNDING_BOX_HEIGHT
				};
			}

			if (this.intersect(boundingBox, mouseRect)) {
				return i;
			}
		}

		return -1;
	}

	/** Define hitbox to be full rectangle */
	isWithin(mousePanel) {
		const handles = this.handle;
		const coords = [
			this.state.endpointLeft || handles[0].getCoords(),
			this.state.endpointRight || handles[1].getCoords()
		];

		// couldn't find one or more handles
		if (!coords[0] || !coords[1]) return false;

		if (
			this.isWithinFibLines(mousePanel, coords) >= 0 ||
			this._isWithinLineTool(mousePanel)
		) {
			return true;
		}
	}

	getLevels(lastY, currY) {
		const range = Math.abs(lastY - currY);
		const vals = this.params.style.thresholds;

		const valuesToShow = [
			0,
			vals[1] * range,
			vals[2] * range,
			vals[3] * range,
			vals[4] * range,
			vals[5] * range
		];

		if (this.params.showExtensions) {
			valuesToShow.push(
				vals[6] * range,
				vals[7] * range,
				vals[8] * range,
				vals[9] * range
			);
		}

		return valuesToShow;
	}

	getFibHeights(fibLevels) {
		// Zero is necessary
		const heights = [0];
		for (let i = 0; i < fibLevels.length - 1; i++) {
			// The +1 is to ensure the color bar covers the white line, tinting it
			heights.push(fibLevels[i + 1] - fibLevels[i] + 1);
		}

		return heights;
	}

	render() {
		const coords = this.handle.map(h => h.getCoords());

		if (!coords[0] || !coords[1]) return;

		this.levelLines.x = coords[0][0];
		const ctx = this.panel.rootToolContext;
		const upSlope = coords[0][1] < coords[1][1];
		let textPadding = -5;
		const fibLevels = this.getLevels(coords[0][1], coords[1][1]);

		ctx.save();
		ctx.beginPath();
		ctx.font = `${this.params.style.label.font.weight} ${this.params.style.label.font.size}px ${this.params.style.label.font.family}`;
		ctx.textAlign = 'right';
		ctx.lineWidth = this.params.style.width;

		// When the retracement is rendered pointing left
		this.levelLines.isFlipped = coords[1][0] - coords[0][0] < 0;
		if (this.levelLines.isFlipped) {
			ctx.textAlign = 'left';
			textPadding = 5;
		}

		// control line
		ctx.strokeStyle = '#ccc';
		ctx.setLineDash([2, 3]);
		ctx.moveTo(coords[0][0], coords[0][1]);
		ctx.lineTo(coords[1][0], coords[1][1]);
		ctx.stroke();

		// Reset line dashed setting back to solid
		ctx.setLineDash([]);

		const heights = this.getFibHeights(fibLevels);
		const { yAxis } = this.panel;
		let height;
		let fillYValue;

		// Draw Fib lines
		for (let i = 0; i < fibLevels.length; i++) {
			// Start new path so only one line is drawn per pass through loop
			ctx.beginPath();
			const val = upSlope ? -fibLevels[i] : fibLevels[i];
			const prevVal = upSlope ? -fibLevels[i - 1] : fibLevels[i - 1];

			ctx.fillStyle = this.params.style.background.color[i];
			ctx.strokeStyle = this.params.style.color[i];
			ctx.moveTo(coords[0][0], subpixel.px(coords[1][1] + val));
			height = heights[i];

			this.levelLines.y[i] = subpixel.px(coords[1][1] + val);

			fillYValue = upSlope
				? subpixel.px(coords[1][1] + prevVal - heights[i])
				: subpixel.px(coords[1][1] + prevVal);

			if (this.levelLines.isFlipped) {
				ctx.lineTo(0, subpixel.px(coords[1][1] + val));
				ctx.fillRect(0, fillYValue, Math.abs(coords[0][0]), height);
			} else {
				ctx.lineTo(
					this.panel.size.width + this.panel.size.padding.left,
					subpixel.px(coords[1][1] + val)
				);
				ctx.fillRect(
					coords[0][0],
					fillYValue,
					this.panel.size.width +
						this.panel.size.padding.left +
						Math.abs(coords[0][0]),
					height
				);
			}

			// Get yAxis value for label
			const yAxisValue = yAxis.getFormatValue(
				yAxis.primaryScale.invert(subpixel.px(coords[1][1] + val)),
				yAxis.getFormatString(yAxis.primaryScale, 5)
			);

			// Use specific label color if defined, otherwise use the section color
			ctx.fillStyle = this.params.style.label.color || this.params.style.color[i];
			// Draw label
			ctx.fillText(
				`${this._getFibFormatValue(this.params.style.thresholds[i])} (${yAxisValue})`,
				coords[0][0] + textPadding,
				subpixel.px(coords[1][1] + val + 3)
			);

			// Draw the line from path specified above
			ctx.stroke();
		}
		ctx.restore();

		this.handle[0].render();
		this.handle[1].render();

		this.panel.core.onToolRender(this);
	}
}
